import { InjectionKey } from "vue";
import {
  createStore,
  useStore as baseUseStore,
  Store,
  createLogger,
} from "vuex";
import { makeUserModule } from "@/store/user";
import { RootState } from "@/store/types";
import { makeSpansModule } from "@/store/spans";
import { makeTracesModule } from "@/store/traces";
import { makeTraceModule } from "@/store/trace";
import { makeDocsModule } from "@/store/docs";
import { makeLogsModule } from "@/store/logs";
import { makeMetricsModule } from "@/store/metrics";
import { makeFiltersModule } from "@/store/filters";
import { makeUrlModule } from "@/store/url";
import { makeSessionModule } from "@/store/session";

export type AppStore = Store<RootState>;

/**
 * Factory function for creating an application store. This function should
 * only be called externally in tests as the store is a singleton within the
 * application accessible by the store const or useStore composition function.
 * @param inProduction - Set to true if the environment is production
 * @param enableDebugLogging - Set to true if you need store debut logs
 * @returns store configured for use in the application.
 */
export function makeStore(
  inProduction = false,
  enableDebugLogging = false
): AppStore {
  return createStore({
    // https://vuex.vuejs.org/guide/strict.html#strict-mode
    strict: !inProduction,
    plugins: !inProduction && enableDebugLogging ? [createLogger()] : [],
    modules: {
      user: makeUserModule(),
      spans: makeSpansModule(),
      traces: makeTracesModule(),
      trace: makeTraceModule(),
      docs: makeDocsModule(),
      logs: makeLogsModule(),
      metrics: makeMetricsModule(),
      filters: makeFiltersModule(),
      url: makeUrlModule(),
      session: makeSessionModule(),
    },
  });
}

// Store used by the application is a singleton typed by an injection key.
export const store = makeStore(process.env.VUE_APP_BUILD_MODE === "prod");
export const key: InjectionKey<AppStore> = Symbol();

/**
 * Simple wrapper function for accessing the singleton store outside of a Vue
 * component setup function. This wrapping function makes it easier to mock
 * access to the store during testing.
 * @returns store singleton created for the application during initialization.
 */
export function getStore(): AppStore {
  return store;
}

/**
 * Simple wrapper function around vuex's useStore function that is used in the
 * Composition API to properly type to the store for this application. For more
 * detail see:
 *
 * https://next.vuex.vuejs.org/guide/typescript-support.html#simplifying-usestore-usage
 * @returns store object typed to the specific store within this application.
 */
export function useStore(): AppStore {
  return baseUseStore(key);
}
